import { FC, useEffect, useRef, useState } from 'react';

import { VideoSlice, VideoSliceDefault } from '@/types';
import * as prismicH from '@prismicio/helpers';

import classNames from 'classnames';
import { Image } from '@/components';
import { getSlicePadding } from '@/utils';
import { VideoIframe, VideoOptimizer } from './Video.component';
import { getVideoWrapperStyle, hasVideoMaxHeight } from './Video.components';
import { useVideoNoPosterReveal } from './useVideoNoPosterReveal';
import { useVideoPlaybackPreserve } from './useVideoPlaybackPreserve';
import {
  isBackgroundVideo,
  isYouTubeUrl,
  resolveControlMode,
} from './videoSource';

import playIcon from '../../public/images/video-icons/play.svg';
import pauseIcon from '../../public/images/video-icons/pause.svg';
import style from './video.module.scss';

const DefaultVariation: FC<{
  slice: VideoSlice & VideoSliceDefault;
  aspectRatio: string;
  isStoreVisible?: boolean;
}> = ({ slice, aspectRatio, isStoreVisible = true }) => {
  const marginBottom = slice?.primary?.bottom_spacing;
  const videoSrc = prismicH.asLink(slice?.primary?.video_content);
  const poster = slice?.primary?.preview_image;
  const posterUrl = prismicH.asImageSrc(poster);
  const paddingSection = slice?.primary?.padding_section;
  const background = slice?.primary?.section_background_color ?? '';
  const autoplay = Boolean(slice?.primary?.autoplay);
  const videoOnFullWidth = slice?.primary.video_on_full_width;

  const controlMode = resolveControlMode(slice?.primary?.controls);
  const isSimplified = controlMode === 'Simplified';
  const isHide = controlMode === 'Hide';
  const isDetailed = controlMode === 'Detailed';
  const isYouTube = isYouTubeUrl(videoSrc);
  const isBackground = isBackgroundVideo(controlMode, autoplay);

  const hasPoster = Boolean(posterUrl);
  const hasVideoWithoutPoster = Boolean(videoSrc) && !hasPoster;
  const wrapperStyle = getVideoWrapperStyle(slice.primary ?? {}, {
    aspectRatio,
    noPosterFill: hasVideoWithoutPoster ? '#000' : undefined,
    posterUrl,
  });
  const hasMaxHeight = hasVideoMaxHeight(slice.primary ?? {});

  const [play, setPlay] = useState(autoplay && isStoreVisible);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  const {
    handleVideoPlay,
    handleVideoReady,
    isVideoVisible,
    skipRevealTransition,
  } = useVideoNoPosterReveal({
    hasPoster,
    hasVideoWithoutPoster,
    videoRef,
    videoUrl: videoSrc || '',
  });

  const shouldPlay = autoplay && isStoreVisible;
  const shouldPreservePlayback = isStoreVisible && (autoplay || play);
  const { skipRevealOnRestore } = useVideoPlaybackPreserve(
    videoRef,
    videoSrc || '',
    shouldPreservePlayback,
  );

  const revealInstant =
    isVideoVisible && (skipRevealTransition || skipRevealOnRestore);
  const allowAutoplay = autoplay && isStoreVisible && !skipRevealOnRestore;

  // Black opacity hold is only for background/autoplay without poster.
  // Detailed/Simplified need the video visible so native/custom controls show.
  const useNoPosterReveal = hasVideoWithoutPoster && isHide;
  const showVideoFrame = !useNoPosterReveal || isVideoVisible;

  useEffect(() => {
    if (isYouTube || !videoRef.current) {
      return;
    }

    if (!isStoreVisible) {
      videoRef.current.pause();
      if (autoplay) {
        setPlay(false);
      }
      return;
    }

    if (shouldPlay) {
      void videoRef.current
        .play()
        .then(() => {
          handleVideoPlay();
          setPlay(true);
        })
        .catch(() => undefined);
    }
  }, [autoplay, isStoreVisible, isYouTube, shouldPlay]);

  const handleSimplifiedToggle = () => {
    if (!isSimplified || !videoRef.current) {
      return;
    }

    if (play) {
      videoRef.current.pause();
      setPlay(false);
      return;
    }

    void videoRef.current.play();
    setPlay(true);
  };

  const wrapperClassName = classNames(style.video__wrapper, {
    [style.video__wrapper_not_image]: !videoSrc && !poster?.url,
    [style.video__wrapper_container]: !videoOnFullWidth,
    [style.video__wrapper_constrained]: hasMaxHeight,
    [style.video__wrapper_no_poster]: useNoPosterReveal,
    [style.video__wrapper_hide_controls]: isHide,
    [style.video__wrapper_simplified]: isSimplified,
    [style.video__wrapper_detailed]: isDetailed,
  });

  const renderNativeVideo = () => (
    // Simplified: click anywhere toggles play; keyboard handled by <video>
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      className={wrapperClassName}
      onClick={isSimplified ? handleSimplifiedToggle : undefined}
      style={wrapperStyle}
    >
      <VideoOptimizer
        alt="Video content"
        ariaLabel="Video player with custom controls"
        aspectRatio={aspectRatio}
        autoplay={allowAutoplay}
        controls={controlMode}
        enableKeyboardControls={isSimplified}
        isDecorative={isBackground}
        loop={true}
        muted={true}
        onCanPlay={handleVideoReady}
        onLoadedData={handleVideoReady}
        onPause={() => setPlay(false)}
        onPlay={() => {
          handleVideoPlay();
          setPlay(true);
        }}
        onPlayPause={isSimplified ? handleSimplifiedToggle : undefined}
        playsInline={true}
        poster={poster}
        preload={
          isDetailed || useNoPosterReveal
            ? 'auto'
            : autoplay
            ? 'metadata'
            : 'none'
        }
        videoClass={classNames({
          [style.video_ready]: showVideoFrame,
          [style.video_ready_instant]: revealInstant || isDetailed,
          [style.video__disabled_interaction]: isBackground,
        })}
        videoRef={videoRef}
        videoUrl={videoSrc || ''}
      />
      {isSimplified && (
        <div
          aria-hidden="true"
          className={classNames(style.overlay, {
            [style.play__state]: play,
            [style.pause__state]: !play,
          })}
        >
          <figure>
            <Image
              alt=""
              className={style.icon}
              height={60}
              src={play ? pauseIcon : playIcon}
              width={60}
            />
          </figure>
        </div>
      )}
    </div>
  );

  const renderYouTubeVideo = () => (
    <div className={wrapperClassName} style={wrapperStyle}>
      {isStoreVisible ? (
        <VideoIframe
          aspectRatio={aspectRatio}
          autoplay={autoplay && isStoreVisible}
          className={classNames({
            [style.video__disabled_interaction]: isBackground,
          })}
          controls={controlMode}
          loop={true}
          poster={poster}
          title="YouTube video player"
          videoUrl={videoSrc || ''}
        />
      ) : (
        poster && (
          <VideoIframe
            autoplay={false}
            controls={controlMode}
            poster={poster}
            title="YouTube video player"
            videoUrl=""
          />
        )
      )}
    </div>
  );

  const renderVideoContent = () => {
    if (isYouTube) {
      return renderYouTubeVideo();
    }

    return renderNativeVideo();
  };

  return (
    <section
      className={classNames(style.video, getSlicePadding(paddingSection), {
        ['spacing_bottom']: marginBottom,
      })}
      hidden={!isStoreVisible}
      style={{ background }}
    >
      <div
        className={classNames(style.video__container_full_width, {
          [style.video__container]: !videoOnFullWidth,
        })}
      >
        {renderVideoContent()}
      </div>
    </section>
  );
};

export default DefaultVariation;
